import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { api, conferenceLabel, type Booking, type Me } from '../api';
import { ChangeMeeting } from '../components/ChangeMeeting';
import { ErrorBox, Loading } from '../components/bk';
import { HOST_TZ, tzName } from '../components/TimezoneSelect';

export function BookingDetail() {
  const { id } = useParams();
  const me = useOutletContext<Me | null>();
  const [b, setB] = useState<Booking | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const tz = me?.timezone ?? HOST_TZ;

  useEffect(() => {
    api.get<Booking>(`/api/bookings/${id}`).then(setB).catch((e) => setError(e.message));
  }, [id]);
  if (!b) return error ? <ErrorBox error={error} /> : <Loading />;

  const s = DateTime.fromISO(b.start).setZone(tz);
  const e = DateTime.fromISO(b.end).setZone(tz);
  const past = DateTime.fromISO(b.end) < DateTime.now();

  return (
    <>
      <p><Link to="/bookings">‹ Meetings</Link></p>
      <section className="card">
        <div className="row between">
          <h1 style={{ margin: 0 }}>{b.inviteeName}</h1>
          {b.status === 'canceled' && <span className="badge err">Canceled</span>}
        </div>
        <p className="muted">{b.linkTitle}</p>
        {notice && <div className="alert ok">{notice}</div>}
        <p className="when" style={{ fontSize: 17 }}>{s.toFormat('cccc, LLLL d')} · {s.toFormat('h:mm a')} – {e.toFormat('h:mm a ZZZZ')}</p>
        <ul className="list small">
          <li><strong>Email:</strong> <a href={`mailto:${b.inviteeEmail}`}>{b.inviteeEmail}</a></li>
          {b.additionalGuests.length > 0 && <li><strong>Guests:</strong> {b.additionalGuests.join(', ')}</li>}
          <li>
            <strong>{conferenceLabel[b.conference.type]}:</strong>{' '}
            {b.conference.joinUrl ? <a href={b.conference.joinUrl} target="_blank" rel="noreferrer">Join link</a> : null}
            {b.conference.dialIn && <> Dial {b.conference.dialIn}{b.conference.passcode && `, code ${b.conference.passcode}`}</>}
          </li>
          <li>
            <strong>Client's local time:</strong>{' '}
            {b.inviteeTimezone && b.inviteeTimezone !== tz
              ? `${DateTime.fromISO(b.start).setZone(b.inviteeTimezone).toFormat('h:mm a ZZZZ')} (${tzName(b.inviteeTimezone)})`
              : `same as yours (${tzName(tz)})`}
          </li>
          {b.inviteeNotes && <li><strong>Notes:</strong> {b.inviteeNotes}</li>}
          {b.cancelReason && <li><strong>Cancel reason:</strong> {b.cancelReason}</li>}
        </ul>
      </section>

      {b.status === 'confirmed' && !past && (
        <section className="card">
          <h2>Change this meeting</h2>
          <p className="muted small">Attendees get an updated calendar invite and an email from eBookMe.</p>
          <ChangeMeeting
            timezone={tz}
            allowCustomTime
            fetchSlots={(from, to) => api.get<{ slots: string[] }>(`/api/bookings/${b.id}/slots?from=${from}&to=${to}`).then((r) => r.slots)}
            onReschedule={async (start, reason, force) => {
              setB(await api.post<Booking>(`/api/bookings/${b.id}/reschedule`, { start, reason, force }));
              setNotice('Rescheduled. Attendees have been notified.');
            }}
            onCancel={async (reason) => {
              setB(await api.post<Booking>(`/api/bookings/${b.id}/cancel`, { reason }));
              setNotice('Canceled. Attendees have been notified.');
            }}
          />
        </section>
      )}

      {b.history && (
        <section className="card">
          <h3>History</h3>
          <ul className="list small">
            {b.history.map((h, i) => (
              <li key={i}>
                <strong>{h.action}</strong> by {h.by} · {DateTime.fromISO(h.at).setZone(tz).toFormat('LLL d, h:mm a')}
                {h.detail && <div className="muted">{h.detail}</div>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
